/**
 * Reporting periods for the BD tracker. Every boundary is a Central midnight:
 * the container runs UTC, and a Sunday-evening lunch in Dallas belongs to that
 * week. A period ends now.
 */
import { formatInTimeZone } from "date-fns-tz";
import { CENTRAL_TZ, addDaysKey, tzMidnightUtc } from "./weeks";

export const BD_PERIODS = ["week", "month", "quarter", "ytd"] as const;
export type BdPeriod = (typeof BD_PERIODS)[number];
export const BD_PERIOD_TABS: Record<BdPeriod, string> = { week: "This week", month: "This month", quarter: "Quarter", ytd: "YTD" };

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const pad = (n: number) => String(n).padStart(2, "0");

export function isBdPeriod(x: unknown): x is BdPeriod {
  return typeof x === "string" && (BD_PERIODS as readonly string[]).includes(x);
}

export interface PeriodBounds {
  period: BdPeriod;
  /** YYYY-MM-DD in Central. Compare date-only values (origination dates) against this. */
  startKey: string;
  /** UTC instant (ISO, Z) of startKey's Central midnight. Compare timestamps against this. */
  start: string;
  /** "This week", "Sep", "Q3" or "2026". */
  label: string;
  yearStartKey: string;
  yearStart: string;
  /** The earlier of start and yearStart: a week that began in December still needs this year's YTD rows. */
  fetchSince: string;
}

export function periodBounds(period: BdPeriod, now: Date, tz: string = CENTRAL_TZ): PeriodBounds {
  const today = formatInTimeZone(now, tz, "yyyy-MM-dd");
  const [y, m] = today.split("-").map(Number);
  let startKey: string;
  let label: string;
  if (period === "week") {
    const isoDow = Number(formatInTimeZone(now, tz, "i"));
    startKey = addDaysKey(today, -(isoDow - 1));
    label = "This week";
  } else if (period === "month") {
    startKey = `${y}-${pad(m)}-01`;
    label = MONTHS[m - 1];
  } else if (period === "quarter") {
    const qm = Math.floor((m - 1) / 3) * 3 + 1;
    startKey = `${y}-${pad(qm)}-01`;
    label = `Q${(qm - 1) / 3 + 1}`;
  } else {
    startKey = `${y}-01-01`;
    label = String(y);
  }
  const yearStartKey = `${y}-01-01`;
  const start = tzMidnightUtc(startKey, tz);
  const yearStart = tzMidnightUtc(yearStartKey, tz);
  return { period, startKey, start, label, yearStartKey, yearStart, fetchSince: startKey < yearStartKey ? start : yearStart };
}
